import React, { useCallback } from 'react';

import { Typography } from '@mui/material';
import { LucideIconButton } from 'src/components/generic';
import { InlineEditField } from 'src/components/generic/InlineEditInput';
import type { Apollo } from 'src/gql/generated';
import { useUpdateBoard } from 'src/hooks/boards';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  labelWrapper: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  nameContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.5),
    minHeight: 40,
  },
  nameInput: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 700,
  },
  nameText: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 700,
  },
  titleIcon: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    alignItems: 'center',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    padding: theme.spacing(0.5),
  },
}));

export type BoardNameSectionBoard = Pick<Apollo.Board, 'color' | 'icon' | 'id' | 'name'>;

interface BoardNameSectionProps {
  board: BoardNameSectionBoard;
}

export function BoardNameSection({ board }: BoardNameSectionProps): JSX.Element {
  const { color, icon, id: boardId, name } = board;
  const { classes } = useStyles();
  const { updateBoard } = useUpdateBoard();

  const handleSave = useCallback(
    (newName: string) => {
      if (!newName) return;
      updateBoard(boardId, { color, icon, name: newName });
    },
    [boardId, color, icon, updateBoard]
  );

  return (
    <div className={classes.container}>
      <div className={classes.labelWrapper}>
        <Typography variant="subtitle1">Board Name</Typography>
      </div>
      <div className={classes.nameContainer}>
        <LucideIconButton
          className={classes.titleIcon}
          selectedColor={color}
          size={36}
          value={icon}
          onChange={newIcon => updateBoard(boardId, { color, icon: newIcon, name })}
          onColorChange={newColor => updateBoard(boardId, { color: newColor, icon, name })}
        />
        <InlineEditField
          classes={{ display: classes.nameText, input: classes.nameInput }}
          iconSize={20}
          value={name}
          onSave={handleSave}
        />
      </div>
    </div>
  );
}
